import { useCallback, useEffect, useRef, useState } from "react";
import type { CncMachineProfile } from "../../../shared/api-contract";
import { useAuth } from "../../../features/auth/context/AuthContext";
import { getWorkspaceCncProfiles, saveWorkspaceCncProfiles } from "../../../features/cnc/cncProfilesApi";
import { loadCncShelf, saveCncShelf, validateCncProfile } from "./cncProfile";
import type { CncProfileShelf, KeyValueStore } from "./cncProfile";
import {
  loadWorkspacePick,
  onlySelectionChanged,
  remapDefault,
  saveWorkspacePick,
  shelfFromWorkspace,
  storedButUnusable,
  workspaceSaveBody,
} from "./cncProfileStore";

/**
 * The CNC dialog's machine profiles, from whichever store is in use (see
 * `cncProfileStore.ts`): the workspace's shared list when the user is signed
 * into one, else this browser's shelf.
 *
 * Workspace saves are debounced (the profile name box commits per keystroke)
 * and serialised: one PUT at a time, each carrying the `updatedAt` the last one
 * returned, so the dialog never trips its own conflict check. A save the
 * server refuses keeps the edit on screen for this session and says so; a
 * conflict (someone else saved first) offers a reload.
 */

export type CncProfileStoreKind = "workspace" | "browser";

export interface CncProfilesState {
  shelf: CncProfileShelf | null;
  setShelf: (next: CncProfileShelf) => void;
  kind: CncProfileStoreKind;
  /** The workspace's name, in workspace mode. */
  workspaceName: string | null;
  loading: boolean;
  /** Could not load or save: a sentence to show. */
  error: string | null;
  /** Why the browser shelf is in use although the user is in a workspace. */
  fallback: string | null;
  /** May this user change the workspace's list. Always true for the browser. */
  canEdit: boolean;
  saving: boolean;
  /** An edit to the workspace's list has not been saved yet (waiting, refused, or not allowed). */
  unsaved: boolean;
  /**
   * The workspace has no stored list, so the dialog opened on this browser's
   * profiles or on the shop's sample. Nothing is saved until an editor changes
   * a profile or saves them (`saveNow`).
   */
  seeded: "browser" | "sample" | null;
  /**
   * The workspace has stored profiles but NONE passes today's checks (they are
   * in `left`). The shelf on screen is this browser's or the shop's sample,
   * used for this export only: no edit, default or "save" writes it to the
   * workspace — that would delete the stored ones — until `replaceStored`.
   */
  storedUnusable: { as: "browser" | "sample" } | null;
  /** Workspace, `storedUnusable` only: the explicit, confirmed "delete the stored profiles and save these". */
  replaceStored: () => void;
  /** The workspace's default profile (the one every member opens on). */
  workspaceDefault: string | null;
  /** Stored profiles left out because they fail today's checks. */
  left: { name: string; errors: string[] }[];
  /** Workspace: save the shelf on screen now (also the "save these to the workspace" action). */
  saveNow: () => void;
  /** Workspace: make the selected profile the one every member opens on. */
  makeDefault: () => void;
  /** Workspace: throw away local edits and read the list again. */
  reload: () => void;
  /** The shop's sample profile (used when no store has one yet) could not be had. */
  profileError: string | null;
}

const SAVE_DEBOUNCE_MS = 700;

function browserKeyValue(): KeyValueStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function useCncProfiles(loadDefault: () => Promise<CncMachineProfile>): CncProfilesState {
  const { activeWorkspace, isAuthenticated, loading: authLoading } = useAuth();
  const workspaceId = isAuthenticated && activeWorkspace ? activeWorkspace.workspaceId : null;

  const [shelf, setShelfState] = useState<CncProfileShelf | null>(null);
  const [kind, setKind] = useState<CncProfileStoreKind>("browser");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fallback, setFallback] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const [seeded, setSeeded] = useState<"browser" | "sample" | null>(null);
  const [storedUnusable, setStoredUnusableState] = useState<{ as: "browser" | "sample" } | null>(null);
  const [workspaceDefault, setWorkspaceDefault] = useState<string | null>(null);
  const [left, setLeft] = useState<{ name: string; errors: string[] }[]>([]);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);

  // The latest values the save queue reads (a PUT outlives the render that queued it).
  const shelfRef = useRef<CncProfileShelf | null>(null);
  const meta = useRef<{ updatedAt: string | null; defaultName: string | null }>({ updatedAt: null, defaultName: null });
  const queue = useRef<{ timer: number | null; busy: boolean; again: boolean }>({ timer: null, busy: false, again: false });
  /** Read by the save queue: while set, nothing is written over the workspace's stored (all-invalid) list. */
  const protectStored = useRef(false);
  const setStoredUnusable = (v: { as: "browser" | "sample" } | null) => {
    protectStored.current = !!v;
    setStoredUnusableState(v);
  };

  const show = (next: CncProfileShelf | null) => {
    shelfRef.current = next;
    setShelfState(next);
  };

  /** The shop's sample from the viewer, used when no store has a profile yet. */
  const sample = useCallback(async (): Promise<CncProfileShelf | null> => {
    try {
      const p = await loadDefault();
      const check = validateCncProfile(p);
      if (check.ok) return { selected: check.profile.name, profiles: [check.profile] };
      setProfileError(`The viewer's sample profile did not pass the checks: ${check.errors.join("; ")}`);
    } catch (e) {
      setProfileError(`Could not load the machine profile: ${(e as Error)?.message || "unknown error"}`);
    }
    return null;
  }, [loadDefault]);

  // ── Load ──
  useEffect(() => {
    if (authLoading) return;
    let live = true;
    setLoading(true);
    setError(null);
    setProfileError(null);
    (async () => {
      const browserShelf = loadCncShelf();
      setSeeded(null);
      setStoredUnusable(null);
      setUnsaved(false);
      if (!workspaceId) {
        setKind("browser");
        setFallback(null);
        setCanEdit(true);
        const s = browserShelf ?? (await sample());
        if (live) show(s);
        return;
      }
      try {
        const list = await getWorkspaceCncProfiles(workspaceId);
        if (!live) return;
        setKind("workspace");
        setFallback(null);
        setCanEdit(list.canEdit);
        meta.current = { updatedAt: list.updatedAt, defaultName: list.defaultProfileId };
        setWorkspaceDefault(list.defaultProfileId);
        const read = shelfFromWorkspace(list, loadWorkspacePick(workspaceId, browserKeyValue()));
        setLeft(read.left);
        if (read.shelf) {
          show(read.shelf);
        } else {
          // No usable stored profile: open on this browser's profiles (how the
          // dialog worked before the workspace store), else the shop's sample.
          // Nothing stored yet ⇒ seeded: saved once an editor says so or edits.
          // Stored but all failing today's checks ⇒ used for this export only,
          // and never saved over them without the explicit replace.
          const s = browserShelf ?? (await sample());
          if (!live) return;
          const as = browserShelf ? "browser" : "sample";
          if (storedButUnusable(list, read)) setStoredUnusable(s ? { as } : null);
          else setSeeded(s ? as : null);
          show(s);
        }
      } catch (e) {
        if (!live) return;
        // Offline, signed out, or the server refused: this browser's shelf, as before.
        setKind("browser");
        setCanEdit(true);
        setFallback(`The workspace's saved profiles could not be read (${(e as Error)?.message || "no answer"}), so this browser's are in use.`);
        const s = browserShelf ?? (await sample());
        if (live) show(s);
      }
    })().finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [authLoading, workspaceId, sample, generation]);

  // ── Save (workspace) ──
  const flush = useCallback(async () => {
    const q = queue.current;
    if (q.timer !== null) { window.clearTimeout(q.timer); q.timer = null; }
    if (q.busy) { q.again = true; return; }
    if (!workspaceId || protectStored.current) return;
    q.busy = true;
    setSaving(true);
    try {
      do {
        q.again = false;
        const s = shelfRef.current;
        if (!s) break;
        const res = await saveWorkspaceCncProfiles(workspaceId, workspaceSaveBody(s, meta.current.defaultName, meta.current.updatedAt));
        meta.current = { updatedAt: res.updatedAt, defaultName: res.defaultProfileId };
        setWorkspaceDefault(res.defaultProfileId);
        setCanEdit(res.canEdit);
        setLeft(res.invalid);
        setError(null);
        setSeeded(null);
        if (!q.again) setUnsaved(false);
      } while (q.again);
    } catch (e) {
      // The API client drops the HTTP status, so the server's own sentence is all
      // there is to tell a conflict (409) from a refusal (400/403); show it whole.
      const why = ((e as Error)?.message || "no answer").trim().replace(/[.\s]*$/, ".");
      setError(`Not saved to the workspace: ${why} Your change is used for this export only.`);
    } finally {
      q.busy = false;
      setSaving(false);
    }
  }, [workspaceId]);

  const schedule = useCallback(() => {
    const q = queue.current;
    if (q.timer !== null) window.clearTimeout(q.timer);
    q.timer = window.setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
  }, [flush]);

  // A pending save still goes out when the dialog closes.
  useEffect(() => () => {
    if (queue.current.timer !== null) void flush();
  }, [flush]);

  const setShelf = useCallback((next: CncProfileShelf) => {
    const prev = shelfRef.current;
    show(next);
    if (kind === "browser") {
      saveCncShelf(next);
      return;
    }
    if (workspaceId) saveWorkspacePick(workspaceId, next.selected, browserKeyValue());
    if (prev && onlySelectionChanged(prev, next)) return; // a pick, not an edit
    if (prev) meta.current.defaultName = remapDefault(prev, next, meta.current.defaultName);
    setWorkspaceDefault(meta.current.defaultName);
    setUnsaved(true);
    if (canEdit) schedule();
  }, [kind, workspaceId, canEdit, schedule]);

  const makeDefault = useCallback(() => {
    const s = shelfRef.current;
    if (!s || kind !== "workspace" || !canEdit) return;
    meta.current.defaultName = s.selected;
    setWorkspaceDefault(s.selected);
    setUnsaved(true);
    void flush();
  }, [kind, canEdit, flush]);

  const saveNow = useCallback(() => {
    if (kind === "workspace" && canEdit) void flush();
  }, [kind, canEdit, flush]);

  const replaceStored = useCallback(() => {
    if (kind !== "workspace" || !canEdit || !shelfRef.current) return;
    setStoredUnusable(null);
    setUnsaved(true);
    void flush();
  }, [kind, canEdit, flush]);

  const reload = useCallback(() => {
    const q = queue.current;
    if (q.timer !== null) { window.clearTimeout(q.timer); q.timer = null; }
    setGeneration((n) => n + 1);
  }, []);

  return {
    shelf,
    setShelf,
    kind,
    workspaceName: kind === "workspace" ? activeWorkspace?.workspaceName ?? null : null,
    loading: loading || authLoading,
    error,
    fallback,
    canEdit,
    saving,
    unsaved,
    seeded,
    storedUnusable,
    replaceStored,
    workspaceDefault,
    left,
    saveNow,
    makeDefault,
    reload,
    profileError,
  };
}
